'use client'

import { useState, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StructuredReviewText, CHANNEL_COLORS, CHANNEL_NAMES, RatingStars } from '@/components/reviews/shared'
import type { ReviewStatsData, ReviewData } from '@/services/property-detail'

interface PropertyReviewsProps {
  propertyId: string
  reviewStats: ReviewStatsData
}

export function PropertyReviews({ propertyId, reviewStats }: PropertyReviewsProps) {
  const t = useTranslations('booking.propertyDetail')
  const [reviews, setReviews] = useState<ReviewData[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const PAGE_SIZE = 5

  const loadReviews = useCallback(async (currentOffset: number) => {
    setLoading(true)
    try {
      const res = await fetch(
        `/api/booking/property-reviews?propertyId=${propertyId}&limit=${PAGE_SIZE}&offset=${currentOffset}`
      )
      const data = await res.json()
      if (data.reviews) {
        setReviews((prev) => currentOffset === 0 ? data.reviews : [...prev, ...data.reviews])
        setHasMore(data.reviews.length === PAGE_SIZE)
        setOffset(currentOffset + PAGE_SIZE)
        setLoaded(true)
      }
    } catch (error) {
      console.error('Failed to load reviews:', error)
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  const handleLoadInitial = () => loadReviews(0)
  const handleLoadMore = () => loadReviews(offset)

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('guestReviews')}
      </h2>

      {/* Rating Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-5 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          {/* Overall score */}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-blue-600 text-white font-bold text-2xl">
              {reviewStats.averageRating.toFixed(1)}
            </div>
            <div>
              <RatingStars rating={reviewStats.averageRating} />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('basedOnReviews', { count: reviewStats.totalCount })}
              </p>
            </div>
          </div>

          {/* Channel badges */}
          {reviewStats.byChannel.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {reviewStats.byChannel.map((ch) => (
                <span
                  key={ch.channel}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    CHANNEL_COLORS[ch.channel] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {CHANNEL_NAMES[ch.channel] || ch.channel}
                  <span className="opacity-70">({ch.count})</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review Cards */}
      {!loaded ? (
        <div className="text-center py-6">
          <Button
            variant="outline"
            onClick={handleLoadInitial}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('loadingReviews')}
              </>
            ) : (
              <>
                {t('showReviews')}
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review, i) => (
            <ReviewCard key={`${review.id}-${i}`} review={review} />
          ))}

          {hasMore && (
            <div className="text-center pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('showMore')
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function ReviewCard({ review }: { review: ReviewData }) {
  const channelColor = CHANNEL_COLORS[review.channel] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  const channelName = CHANNEL_NAMES[review.channel] || review.channel

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Guest avatar placeholder */}
          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-400">
            {review.guest_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {review.guest_name || 'Guest'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(review.review_date).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${channelColor}`}>
            {channelName}
          </span>
          {review.rating && (
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
              {Number(review.rating).toFixed(1)}
            </span>
          )}
        </div>
      </div>
      {review.content && (
        <StructuredReviewText text={review.content} />
      )}
    </div>
  )
}
